import React, { useState } from "react";
import ReactDOM from "react-dom";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import { FAST_COLOR } from "../styles/tokens";
import { FAST_CREATABLE_CATEGORIES, FastCategoryKey, PinIcon, GlobeIcon, FriendsIcon } from "./FastCategoryIcons";
import VenueField from "./VenueField";
import AudienceSelector from "./AudienceSelector";

const FAST_AUDIENCES = [
  { key: "public", label: "Public", sub: "Visible par tous les membres à proximité", icon: GlobeIcon },
  { key: "amis", label: "Mes amis", sub: "Visible uniquement par tes amis", icon: FriendsIcon },
];
const DURATIONS = [15, 30, 45, 60];

const nowHHMM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

type Step = "category" | "place" | "when" | "message";

// Création d'un FAST (ex-QUICK) — quelques secondes, pas un formulaire d'événement. Même chrome
// que LiveRecordModal (modale centrée, fond sombre, sheet blanche arrondie) : familier, cohérent
// avec le reste de l'app. La catégorie avance immédiatement au tap (pas de bouton "suivant" à
// cette étape précise) — c'est ce qui tient l'objectif des 10 secondes.
// Doré = touche, jamais un remplissage : les boutons pleins (Publier, durée sélectionnée) restent
// noir/blanc, le doré ne sert qu'aux contours fins et petits accents (icônes, pin de géoloc).
export default function FastCreateSheet({ onClose, onCreated }: { onClose: () => void; onCreated: (fast: any) => void }) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("category");
  const [category, setCategory] = useState<FastCategoryKey | null>(null);

  const [locationText, setLocationText] = useState("");
  const [venueId, setVenueId] = useState<string | null>(null);
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [locatingMe, setLocatingMe] = useState(false);
  const [locationError, setLocationError] = useState("");

  const [startTime, setStartTime] = useState(nowHHMM());
  const [duration, setDuration] = useState(60);
  const [audience, setAudience] = useState("public");
  const [message, setMessage] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");

  const handleVenueChange = (label: string, vId?: string | null, vLat?: number | null, vLng?: number | null) => {
    setLocationText(label);
    setVenueId(vId ?? null);
    if (vLat != null) setLat(vLat);
    if (vLng != null) setLng(vLng);
  };

  const useMyPosition = () => {
    setLocationError("");
    if (!navigator.geolocation) { setLocationError("Géolocalisation non disponible"); return; }
    setLocatingMe(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        setLocationText("Ma position actuelle");
        setVenueId(null);
        setLat(pos.coords.latitude);
        setLng(pos.coords.longitude);
        setLocatingMe(false);
      },
      () => { setLocationError("Impossible d'accéder à ta position"); setLocatingMe(false); },
      { enableHighAccuracy: false, timeout: 8000 }
    );
  };

  const hasPlace = !!locationText && lat != null && lng != null;

  const publish = async () => {
    if (!category || !hasPlace || publishing) return;
    setPublishing(true);
    setError("");
    try {
      const now = new Date();
      const [h, m] = startTime.split(":").map(Number);
      const startsAt = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m);
      const data = await api.post("/fasts", {
        category, place_label: locationText, venue_id: venueId, latitude: lat, longitude: lng,
        audience, starts_at: startsAt.toISOString(), duration_minutes: duration,
        message: message.trim() || undefined,
      });
      if (data?.fast) onCreated(data.fast);
      else setError(data?.error || "Erreur lors de la publication");
    } catch {
      setError("Erreur de connexion");
    } finally {
      setPublishing(false);
    }
  };

  const sheetHeader = (title: string, onBack?: () => void) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
      {onBack ? (
        <button onClick={onBack} style={{ border: "none", background: "none", cursor: "pointer", padding: 4, color: "#666", fontSize: 13, fontWeight: 600 }}>← Retour</button>
      ) : <span />}
      <span style={{ fontSize: 16, fontWeight: 700, position: "absolute", left: "50%", transform: "translateX(-50%)" }}>{title}</span>
      <button onClick={onClose} aria-label="Fermer" style={{ border: "none", background: "none", cursor: "pointer", padding: 4, color: "#999", display: "flex" }}><X size={20} strokeWidth={2} /></button>
    </div>
  );

  // Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx/PhotoLightbox.tsx :
  // le conteneur mobile de Layout.tsx utilise WebkitOverflowScrolling:"touch", et sur Safari iOS
  // un position:fixed descendant d'un tel ancêtre (ou, ici, de <MapContainer> de react-leaflet,
  // rendu depuis Explore.tsx) se positionne par rapport à TOUT son contenu scrollable plutôt
  // qu'au vrai viewport — d'où le contenu visuellement décalé au lieu d'être centré à l'écran.
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 20, padding: 22, width: "100%", maxWidth: 400, maxHeight: "88vh", overflowY: "auto", boxSizing: "border-box", position: "relative" }}>

        {step === "category" && (
          <>
            <div style={{ textAlign: "center", marginBottom: 4 }}>
              <span style={{ fontSize: 17, fontWeight: 800 }}>Je vais aller quelque part pour…</span>
              <button onClick={onClose} aria-label="Fermer" style={{ position: "absolute", top: 22, right: 22, border: "none", background: "none", cursor: "pointer", padding: 4, color: "#999", display: "flex" }}><X size={20} strokeWidth={2} /></button>
            </div>
            <div style={{ fontSize: 12.5, color: "#999", textAlign: "center", marginBottom: 20 }}>Un tap suffit pour continuer</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {FAST_CREATABLE_CATEGORIES.map(c => (
                <button
                  key={c.key}
                  onClick={() => { setCategory(c.key); setStep("place"); }}
                  style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "18px 10px", borderRadius: 16, border: "1.5px solid #F0F0F0", backgroundColor: "#FAFAFA", cursor: "pointer" }}
                >
                  <span style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: "white", border: `1.5px solid ${FAST_COLOR}44`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <c.Icon color="#0a0a0a" size={20} />
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0a0a0a" }}>{c.label}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {step === "place" && (
          <>
            {sheetHeader("Où ?", () => setStep("category"))}
            <button
              onClick={useMyPosition}
              disabled={locatingMe}
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px 14px", borderRadius: 12, border: `1.5px solid ${FAST_COLOR}`, backgroundColor: `${FAST_COLOR}0F`, cursor: locatingMe ? "default" : "pointer", marginBottom: 14, fontSize: 13.5, fontWeight: 700, color: "#0a0a0a" }}
            >
              <PinIcon color={FAST_COLOR} size={15} /> {locatingMe ? "Localisation en cours…" : "Utiliser ma position actuelle"}
            </button>
            {!!locationError && <div style={{ fontSize: 12, color: "#c0392b", marginBottom: 10 }}>{locationError}</div>}
            <VenueField value={locationText} onChange={handleVenueChange} lang="fr" onNavigate={navigate} />
            <button
              onClick={() => hasPlace && setStep("when")}
              disabled={!hasPlace}
              style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", backgroundColor: hasPlace ? "#0a0a0a" : "#E0E0E0", color: "white", fontSize: 14, fontWeight: 600, cursor: hasPlace ? "pointer" : "default", marginTop: 8 }}
            >
              Continuer
            </button>
          </>
        )}

        {step === "when" && (
          <>
            {sheetHeader("Quand ?", () => setStep("place"))}
            <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 8 }}>Heure de début</div>
            <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 16, boxSizing: "border-box" }} />

            <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 8 }}>Durée, 1h maximum</div>
            <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
              {DURATIONS.map(d => (
                <button
                  key={d}
                  onClick={() => setDuration(d)}
                  style={{
                    flex: 1, padding: "10px 0", borderRadius: 20,
                    border: duration === d ? `1.5px solid ${FAST_COLOR}` : "1.5px solid #E0E0E0",
                    backgroundColor: duration === d ? "#0a0a0a" : "white",
                    color: duration === d ? FAST_COLOR : "#666",
                    fontSize: 13, fontWeight: 700, cursor: "pointer",
                  }}
                >
                  {d} min
                </button>
              ))}
            </div>

            <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 8 }}>Qui peut voir ce FAST ?</div>
            <AudienceSelector value={audience} onChange={setAudience} options={FAST_AUDIENCES} />

            <button onClick={() => setStep("message")} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
              Continuer
            </button>
          </>
        )}

        {step === "message" && (
          <>
            {sheetHeader("Un mot ? (optionnel)", () => setStep("when"))}
            <textarea
              value={message} onChange={e => setMessage(e.target.value)} maxLength={150} rows={3}
              placeholder="Ex : Quelqu'un se joint à moi ?"
              style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", resize: "none", boxSizing: "border-box", marginBottom: 16, fontFamily: "inherit" }}
            />
            {!!error && <div style={{ fontSize: 12.5, color: "#c0392b", marginBottom: 12 }}>{error}</div>}
            <button
              onClick={publish}
              disabled={publishing}
              style={{ width: "100%", padding: "14px", borderRadius: 12, border: `1.5px solid ${FAST_COLOR}`, backgroundColor: "#0a0a0a", color: "white", fontSize: 15, fontWeight: 800, cursor: publishing ? "default" : "pointer", opacity: publishing ? 0.7 : 1 }}
            >
              {publishing ? "Publication…" : "Publier"}
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
